// frontend/packages/business-console/src/pages/QuoteEditor.jsx
//
// Work item 2.14 — Quote editor (`/business/:slug/quotes/new` and
// `/business/:slug/quotes/:id/edit`). Spec source:
// `docs/design/quotes-invoices-console.md` §3.1 + §3.2 + §3.7 + §3.8.
//
// Design pattern: WYSIWYG single-pane (NOT split-pane). The edit
// surface is styled to look like the rendered document — business
// header (left) + customer block (right) + line-item spreadsheet +
// totals + notes/terms. Operator types into the document as if
// marking up a printed quote. A "Preview" affordance opens the
// canonical PDF render when the quote has been previously sent
// (quote.pdf.blobUrl); for fresh drafts there's nothing to preview
// yet (PDF only lands on Send).
//
// State:
//   - /quotes/new   → fresh empty draft
//   - /quotes/:id/edit → load existing draft (refuse non-draft state)
//
// Actions:
//   - Cancel → navigate back to /quotes (with confirm if dirty)
//   - Save draft → POST (new) or PATCH (existing); stays on page,
//     navigates to /quotes/:id/edit if newly created so refreshes work
//   - Send → save draft first, then POST /:id/send (confirm modal
//     mirrors QuoteDetail's send dialog)

import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import {
  Link as RouterLink,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { BusinessContext } from '../contexts/BusinessContext';
import CustomerPicker from './Quotes/CustomerPicker';
import QuoteLineItemsEditor, { newRow } from './Quotes/QuoteLineItemsEditor';
import {
  createQuoteDraft,
  getQuote,
  sendQuote,
  updateQuoteDraft,
} from '../services/quoteService';
import { getCustomer } from '../services/crmService';
import useOfflineOutbox from '../hooks/useOfflineOutbox';
import useOnlineStatus from '../hooks/useOnlineStatus';
import { newClientUuid } from '../lib/outboxStore';
import OfflineStatusChip from '../components/OfflineStatusChip';

const STATE_COLOR = {
  draft: 'default',
  sent: 'info',
  viewed: 'warning',
  accepted: 'success',
  declined: 'error',
  expired: 'default',
  converted: 'success',
  void: 'default',
};

function fmtUsd(cents) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format((cents || 0) / 100);
}

function customerName(customer) {
  if (!customer) return 'the customer';
  if (customer.companyName) return customer.companyName;
  const fn = customer.firstName || '';
  const ln = customer.lastName || '';
  return `${fn} ${ln}`.trim() || 'the customer';
}

function isDemoMode(search) {
  const params = new URLSearchParams(search || '');
  if (params.get('demo') === '1') return true;
  try {
    return localStorage.getItem('pulse.demo') === '1';
  } catch {
    return false;
  }
}

function emptyDraft() {
  return {
    customerId: null,
    title: '',
    lineItems: [newRow(0)],
    notes: '',
    terms: '',
    validUntil: null,
  };
}

export default function QuoteEditor() {
  const { business } = useContext(BusinessContext);
  const navigate = useNavigate();
  const { id } = useParams(); // undefined on /new
  const [searchParams] = useSearchParams();
  const prefillCustomerId = id ? null : searchParams.get('customerId');
  const basePath = business?.slug ? `/business/${business.slug}` : '';
  const isDemo = useMemo(
    () =>
      isDemoMode(typeof window !== 'undefined' ? window.location.search : ''),
    []
  );

  // Form state
  const [form, setForm] = useState(emptyDraft);
  const [customer, setCustomer] = useState(null);
  const [number, setNumber] = useState(null); // Q-#### once saved
  const [state, setState] = useState('draft');
  const [serverId, setServerId] = useState(id || null);

  // 2.19 offline-outbox plumbing. A FRESH Idempotency-Key is minted per
  // save attempt (in saveDraft), NOT one per session. Reusing a single
  // key across edits caused `idempotency_key_reuse_body_mismatch` the
  // moment the body changed (e.g. after toggling tax on a line item). A
  // retry of one logical save still dedupes: the outbox item carries that
  // save's own `clientUuid`, and the sync callback closes over the current
  // `serverId` ref so it knows whether to POST or PATCH when it drains.
  const serverIdRef = useRef(serverId);
  serverIdRef.current = serverId;
  const online = useOnlineStatus();

  const outbox = useOfflineOutbox({
    kind: 'quote',
    sync: async (item) => {
      const targetId = item.serverId || serverIdRef.current;
      const response = targetId
        ? await updateQuoteDraft(targetId, item.payload, {
            idempotencyKey: item.clientUuid,
          })
        : await createQuoteDraft(item.payload, {
            idempotencyKey: item.clientUuid,
          });
      const q = response?.quote;
      if (q) {
        if (!targetId) {
          setServerId(q._id);
          serverIdRef.current = q._id;
          // Hop into the persistent /:id/edit URL so a refresh works.
          navigate(`${basePath}/quotes/${q._id}/edit`, { replace: true });
        }
        setNumber(q.number);
        setState(q.state);
        setForm((prev) => ({
          ...prev,
          lineItems:
            Array.isArray(q.lineItems) && q.lineItems.length > 0
              ? q.lineItems
              : prev.lineItems,
        }));
        setDirty(false);
      }
      return {
        serverId: q?._id || targetId || null,
        serverNumber: q?.number || null,
      };
    },
  });

  // Lifecycle
  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [dirty, setDirty] = useState(false);

  // Load existing draft on /:id/edit
  useEffect(() => {
    if (!id) return;
    if (isDemo) {
      setLoading(false);
      setLoadError(
        new Error(
          'Editing an existing draft is disabled in demo mode. Use the New estimate flow.'
        )
      );
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await getQuote(id);
        if (cancelled) return;
        const q = res.quote;
        if (q.state !== 'draft') {
          setLoadError(
            new Error(
              `This estimate is in '${q.state}' state. Only drafts can be edited.`
            )
          );
          return;
        }
        setNumber(q.number);
        setState(q.state);
        setServerId(q._id);
        setForm({
          customerId: q.customerId,
          title: q.title || '',
          lineItems:
            Array.isArray(q.lineItems) && q.lineItems.length > 0
              ? q.lineItems
              : [newRow(0)],
          notes: q.notes || '',
          terms: q.terms || '',
          validUntil: q.validUntil || null,
        });
        // Customer fetched in parallel; soft-fail if missing.
        // getCustomer returns { customer, activities, ... } — unwrap.
        try {
          const res2 = await getCustomer(q.customerId);
          if (!cancelled && res2?.customer) setCustomer(res2.customer);
        } catch {
          /* ignore */
        }
      } catch (err) {
        if (!cancelled) setLoadError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isDemo]);

  // Pre-fill the Bill-to customer when arriving from the CRM detail page
  // via /quotes/new?customerId=<id>. Soft-fails so a stale link doesn't
  // block draft creation.
  useEffect(() => {
    if (!prefillCustomerId || isDemo) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getCustomer(prefillCustomerId);
        const cust = res?.customer;
        if (cancelled || !cust) return;
        setCustomer(cust);
        setForm((prev) => ({ ...prev, customerId: cust._id }));
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [prefillCustomerId, isDemo]);

  const updateForm = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  };

  const updateCustomer = (c) => {
    setCustomer(c);
    setForm((prev) => ({ ...prev, customerId: c?._id || null }));
    setDirty(true);
  };

  // Live totals (preview; server recomputes on save)
  const totals = useMemo(() => {
    let subtotal = 0;
    let taxTotal = 0;
    for (const li of form.lineItems) {
      const qty = Number(li.quantity) || 0;
      const lineSub = qty * (li.unitPrice || 0);
      const lineTax = li.taxable
        ? Math.round((lineSub * (li.taxRateBps || 0)) / 10000)
        : 0;
      subtotal += lineSub;
      taxTotal += lineTax;
    }
    return { subtotal, taxTotal, total: subtotal + taxTotal };
  }, [form.lineItems]);

  // Validation: can we save? (need customer + at least one line item
  // with a description + nonzero unit price)
  const validation = useMemo(() => {
    const errors = [];
    if (!form.customerId) errors.push('Choose a customer.');
    const usable = form.lineItems.filter(
      (li) => li.description.trim() && (Number(li.quantity) || 0) > 0
    );
    if (usable.length === 0)
      errors.push(
        'Add at least one line item with a description and quantity.'
      );
    return { canSave: errors.length === 0, errors };
  }, [form]);

  const buildPayload = () => ({
    customerId: form.customerId,
    title: form.title || undefined,
    lineItems: form.lineItems.map((li, idx) => ({
      id: li.id,
      position: idx,
      description: li.description.trim(),
      quantity: String(li.quantity || '0'),
      unitPrice: Math.max(0, Math.round(li.unitPrice || 0)),
      taxable: Boolean(li.taxable),
      taxRateBps: li.taxable && li.taxRateBps != null ? li.taxRateBps : null,
    })),
    notes: form.notes || undefined,
    terms: form.terms || undefined,
    validUntil: form.validUntil || undefined,
  });

  const saveDraft = async ({ silent } = {}) => {
    if (isDemo) {
      setToast({
        severity: 'info',
        text: 'Saving is disabled in demo mode. Disable ?demo=1 to test against the live tenant.',
      });
      return null;
    }

    const payload = buildPayload();

    // Fresh idempotency key per save attempt. Reusing one key across
    // edits is what triggered `idempotency_key_reuse_body_mismatch` once
    // the body changed (e.g. adding tax). One logical save's retries are
    // still deduped because the same key rides with this attempt (and, on
    // the offline path, with its outbox item).
    const idempotencyKey = newClientUuid();

    // Offline path (2.19): enqueue to the outbox and return. The
    // useOfflineOutbox hook's `online` listener flushes on reconnect
    // through the shared sync callback above. Send / convert flows
    // are NOT queued — they're disabled in the UI when offline.
    if (!online) {
      await outbox.enqueue({
        clientUuid: idempotencyKey,
        payload,
        route: serverId
          ? `PATCH /v2/crm/quotes/${serverId}`
          : 'POST /v2/crm/quotes',
        serverId: serverId || null,
      });
      setDirty(false);
      if (!silent) {
        setToast({
          severity: 'warning',
          text: "Saved locally. We'll sync as soon as you're back online.",
        });
      }
      return { _id: serverId, number, state, _offline: true };
    }

    setSaving(true);
    setSaveError(null);
    try {
      let response;
      if (serverId) {
        response = await updateQuoteDraft(serverId, payload, {
          idempotencyKey,
        });
      } else {
        response = await createQuoteDraft(payload, {
          idempotencyKey,
        });
      }
      const q = response?.quote;
      if (q) {
        if (!serverId) {
          // Newly created → move to /quotes/:id/edit so refresh works.
          setServerId(q._id);
          serverIdRef.current = q._id;
          navigate(`${basePath}/quotes/${q._id}/edit`, { replace: true });
        }
        setNumber(q.number);
        setState(q.state);
        // Sync recomputed totals from server response.
        setForm((prev) => ({
          ...prev,
          lineItems:
            Array.isArray(q.lineItems) && q.lineItems.length > 0
              ? q.lineItems
              : prev.lineItems,
        }));
        setDirty(false);
      }
      if (!silent) {
        setToast({
          severity: 'success',
          text: `Draft saved${q?.number ? ` (${q.number})` : ''}.`,
        });
      }
      return q;
    } catch (err) {
      // Network/5xx → fall back to outbox so the operator doesn't lose work.
      const httpStatus = err?.response?.status;
      const isTransient =
        !httpStatus ||
        httpStatus >= 500 ||
        err?.code === 'ERR_NETWORK' ||
        err?.message === 'Network Error';
      if (isTransient) {
        await outbox.enqueue({
          clientUuid: idempotencyKey,
          payload,
          route: serverId
            ? `PATCH /v2/crm/quotes/${serverId}`
            : 'POST /v2/crm/quotes',
          serverId: serverId || null,
        });
        setDirty(false);
        if (!silent) {
          setToast({
            severity: 'warning',
            text: "Couldn't reach the server. Saved locally; we'll retry on reconnect.",
          });
        }
        return { _id: serverId, number, state, _offline: true };
      }
      setSaveError(
        err?.response?.data?.error || err?.message || "Couldn't save draft."
      );
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSendConfirm = async () => {
    if (isDemo) {
      setSendDialogOpen(false);
      setToast({
        severity: 'info',
        text: 'Sending is disabled in demo mode.',
      });
      return;
    }
    setSending(true);
    setSaveError(null);
    try {
      let savedId = serverId;
      if (!savedId || dirty) {
        const saved = await saveDraft({ silent: true });
        if (!saved) {
          // Save failed — saveError already set
          setSending(false);
          return;
        }
        savedId = saved._id || serverId;
      }
      await sendQuote(savedId);
      setSendDialogOpen(false);
      setToast({ severity: 'success', text: 'Estimate sent.' });
      // Navigate to detail page where the operator sees the sent state.
      navigate(`${basePath}/quotes/${savedId}`);
    } catch (err) {
      setSaveError(
        err?.response?.data?.error || err?.message || "Couldn't send."
      );
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (loadError) {
    return (
      <Box sx={{ p: 3, maxWidth: 720, mx: 'auto' }}>
        <BackLink to={`${basePath}/quotes`} />
        <Alert severity="error" sx={{ mt: 2 }}>
          {loadError?.response?.data?.error ||
            loadError?.message ||
            "Couldn't load this estimate."}
        </Alert>
      </Box>
    );
  }

  const titleText = serverId ? `Editing ${number || 'draft'}` : 'New estimate';

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        maxWidth: 960,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {/* ── Sticky top bar — keeps Save / Send reachable on a long form ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          position: 'sticky',
          top: { xs: 56, md: 64 },
          zIndex: 2,
          backgroundColor: 'background.default',
          py: 1,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <BackLink
            to={`${basePath}/quotes`}
            confirmIfDirty={dirty}
            navigate={navigate}
          />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            /
          </Typography>
          <Typography sx={{ fontWeight: 700, color: 'text.primary' }}>
            {titleText}
          </Typography>
          {state ? (
            <Chip
              size="small"
              label={state}
              color={STATE_COLOR[state] || 'default'}
              variant="outlined"
              sx={{ textTransform: 'capitalize', fontWeight: 600 }}
            />
          ) : null}
          {isDemo ? (
            <Chip
              size="small"
              label="Demo"
              variant="outlined"
              sx={{ color: 'text.secondary' }}
            />
          ) : null}
          <OfflineStatusChip
            online={online}
            isFlushing={outbox.isFlushing}
            pendingCount={
              outbox.items.filter(
                (it) => it.status === 'pending' || it.status === 'syncing'
              ).length
            }
            failedCount={
              outbox.items.filter((it) => it.status === 'failed').length
            }
            lastSavedAt={outbox.items[0]?.savedAt || null}
          />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            component={RouterLink}
            to={`${basePath}/quotes`}
            size="small"
            disabled={saving || sending}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={
              saving ? <CircularProgress size={14} /> : <SaveOutlinedIcon />
            }
            onClick={() => saveDraft()}
            disabled={!validation.canSave || saving || sending}
          >
            {saving ? 'Saving…' : 'Save draft'}
          </Button>
          <Tooltip title={!online ? "You're offline. Reconnect to send." : ''}>
            <span>
              <Button
                size="small"
                variant="contained"
                startIcon={
                  sending ? (
                    <CircularProgress size={14} />
                  ) : (
                    <SendOutlinedIcon />
                  )
                }
                onClick={() => setSendDialogOpen(true)}
                disabled={!validation.canSave || saving || sending || !online}
              >
                {sending ? 'Sending…' : 'Send'}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {/* ── Validation hints ─────────────────────────────────────── */}
      {validation.errors.length > 0 ? (
        <Alert severity="info" variant="outlined">
          {validation.errors.join('  ·  ')}
        </Alert>
      ) : null}
      {saveError ? <Alert severity="error">{saveError}</Alert> : null}

      {/* ── WYSIWYG document body ────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: { xs: 3, sm: 5 },
          fontFamily:
            "'Liberation Sans', 'Noto Sans', Arial, system-ui, sans-serif",
        }}
      >
        {/* Header: business identity + Q-#### label */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 700,
                color: 'text.primary',
                letterSpacing: '-0.01em',
              }}
            >
              {business?.name || 'Your business'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              The header (logo, address) renders from your business profile on
              send.
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.18em',
                color: 'text.secondary',
                textTransform: 'uppercase',
              }}
            >
              Estimate
            </Typography>
            <Typography
              sx={{
                fontSize: 22,
                fontWeight: 700,
                fontFamily: 'monospace',
                color: 'text.primary',
                mt: 0.5,
              }}
            >
              {number || 'Q-####'}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                mt: 0.5,
              }}
            >
              Issued on save
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Customer picker */}
        <CustomerPicker
          value={customer}
          onChange={updateCustomer}
          disabled={saving || sending}
          isDemo={isDemo}
        />

        {/* Title / job name */}
        <TextField
          fullWidth
          size="small"
          label="Title (optional)"
          value={form.title}
          onChange={(e) => updateForm({ title: e.target.value })}
          disabled={saving || sending}
          sx={{ mt: 3 }}
        />

        <Divider sx={{ my: 3 }} />

        {/* Line items */}
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: 'text.secondary',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            display: 'block',
            mb: 1,
          }}
        >
          Line items
        </Typography>
        <QuoteLineItemsEditor
          lineItems={form.lineItems}
          onChange={(next) => updateForm({ lineItems: next })}
          defaultTaxBps={business?.taxConfig?.defaultTaxRateBps || null}
          disabled={saving || sending}
        />

        {/* Totals */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 3,
          }}
        >
          <Stack
            spacing={0.5}
            sx={{
              minWidth: 260,
              '& > *': {
                display: 'flex',
                justifyContent: 'space-between',
              },
            }}
          >
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Subtotal
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {fmtUsd(totals.subtotal)}
              </Typography>
            </Box>
            {totals.taxTotal > 0 ? (
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Tax
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {fmtUsd(totals.taxTotal)}
                </Typography>
              </Box>
            ) : null}
            <Divider />
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                Total
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 16,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {fmtUsd(totals.total)}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Notes + Terms */}
        <Stack spacing={2}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            size="small"
            label="Notes (visible to customer)"
            value={form.notes}
            onChange={(e) => updateForm({ notes: e.target.value })}
            disabled={saving || sending}
            placeholder="Anything the customer should see — assumptions, follow-up notes, etc."
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            size="small"
            label="Terms"
            value={form.terms}
            onChange={(e) => updateForm({ terms: e.target.value })}
            disabled={saving || sending}
            placeholder="e.g. Net 30. 1.5%/mo on past-due balances."
          />
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Valid until (optional)"
            value={
              form.validUntil
                ? new Date(form.validUntil).toISOString().slice(0, 10)
                : ''
            }
            onChange={(e) =>
              updateForm({
                validUntil: e.target.value
                  ? new Date(e.target.value).toISOString()
                  : null,
              })
            }
            disabled={saving || sending}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </Paper>

      {/* ── Send confirm dialog ─────────────────────────────────── */}
      <Dialog
        open={sendDialogOpen}
        onClose={() => (sending ? null : setSendDialogOpen(false))}
      >
        <DialogTitle>Send {number || 'this estimate'}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This sends the <strong>PDF and accept link</strong> to{' '}
            <strong>{customerName(customer)}</strong>
            {customer?.email ? (
              <>
                {' '}
                (<code>{customer.email}</code>)
              </>
            ) : null}{' '}
            and locks the estimate. You can void it later but you can&apos;t
            unsend.
          </DialogContentText>
          {saveError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {saveError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialogOpen(false)} disabled={sending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSendConfirm}
            disabled={sending}
            startIcon={
              sending ? <CircularProgress size={14} /> : <SendOutlinedIcon />
            }
          >
            {sending ? 'Sending…' : 'Send now'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity || 'info'}
            onClose={() => setToast(null)}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {toast.text}
          </Alert>
        ) : null}
      </Snackbar>
    </Box>
  );
}

function BackLink({ to, confirmIfDirty, navigate }) {
  const handleClick = (e) => {
    if (!confirmIfDirty) return;
    const ok = window.confirm(
      'Discard unsaved changes and go back to the list?'
    );
    if (!ok) {
      e.preventDefault();
    } else if (navigate) {
      e.preventDefault();
      navigate(to);
    }
  };
  return (
    <Tooltip title="Back to estimates">
      <IconButton
        size="small"
        component={RouterLink}
        to={to}
        onClick={handleClick}
        sx={{ color: 'text.secondary' }}
        aria-label="Back to estimates"
      >
        <ArrowBackRoundedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}
